import type { ManagerRole } from '@/store/auth.store';

export type SalesMethod = 'CASH' | 'CARD' | 'TRANSFER' | 'SAFE';

export const SALES_METHODS: SalesMethod[] = ['CASH', 'CARD', 'TRANSFER', 'SAFE'];

export const METHOD_LABEL: Record<SalesMethod, string> = {
  CASH: 'Наличные',
  CARD: 'Карта',
  TRANSFER: 'Перевод',
  SAFE: 'Из сейфа',
};

/** Плашка способа оплаты: фон и текст одного оттенка. */
export const METHOD_TONE: Record<SalesMethod, string> = {
  CASH: 'bg-emerald-50 text-emerald-800',
  CARD: 'bg-sky-50 text-sky-800',
  TRANSFER: 'bg-violet-50 text-violet-800',
  SAFE: 'bg-orange/10 text-orange-700',
};

/** Полоска доли в разбивке по способам. */
export const METHOD_BAR: Record<SalesMethod, string> = {
  CASH: 'bg-emerald-500',
  CARD: 'bg-sky-500',
  TRANSFER: 'bg-violet-500',
  SAFE: 'bg-orange',
};

export const ROLE_LABEL: Record<ManagerRole, string> = {
  MANAGER: 'менеджер',
  HEAD_MANAGER: 'главный',
  SALES_DIRECTOR: 'руководитель',
};

/** Полное название роли — в профиле и карточке сотрудника. */
export const ROLE_FULL: Record<ManagerRole, string> = {
  MANAGER: 'Менеджер',
  HEAD_MANAGER: 'Главный менеджер',
  SALES_DIRECTOR: 'Руководитель отдела продаж',
};

export interface DirectorOverview {
  period: { from: string; to: string; previousFrom: string; previousTo: string };
  turnover: { kopecks: number; payments: number; payers: number; previousKopecks: number; averageKopecks: number };
  safe: {
    percent: number;
    startDate: string;
    inPeriodKopecks: number;
    inflowKopecks: number;
    expensesKopecks: number;
    payoutsKopecks: number;
    balanceKopecks: number;
  };
  expenses: { totalKopecks: number; byMethod: Record<SalesMethod, number> };
  payouts: { kopecks: number; count: number };
  establishments: {
    total: number;
    attachedToManagers: number;
    newInPeriod: number;
    firstPaidInPeriod: number;
    paying: number;
    trial: number;
    grace: number;
    blocked: number;
    noSubscription?: number;
  };
  series: Array<{
    month: string;
    turnoverKopecks: number;
    safeInKopecks: number;
    expensesKopecks: number;
    payoutsKopecks: number;
  }>;
}

export interface RatingRow {
  rank: number;
  id: string;
  name: string;
  email: string;
  code: string;
  role: ManagerRole;
  status: 'active' | 'blocked';
  /** Удалён руководителем: строка за прошлые периоды есть, карточки нет. */
  deleted?: boolean;
  avatarUrl?: string | null;
  establishments: number;
  newEstablishments: number;
  payingEstablishments: number;
  turnoverKopecks: number;
  payments: number;
  commissionKopecks: number;
}

export interface ExpenseCategory {
  id: string;
  name: string;
  color: string | null;
  isActive: boolean;
  sortOrder: number;
}

export interface SalesExpense {
  id: string;
  amountKopecks: number;
  method: SalesMethod;
  comment: string | null;
  spentAt: string;
  category: { id: string; name: string; color: string | null } | null;
  createdBy: { id: string; name: string } | null;
  createdAt: string;
}

export interface ExpensesResponse {
  period: { from: string; to: string };
  items: SalesExpense[];
  totals: {
    totalKopecks: number;
    byMethod: Record<SalesMethod, number>;
    byCategory: Array<{ categoryId: string | null; name: string; color: string | null; kopecks: number }>;
  };
}
